import styled from "@emotion/styled";

import {
  MDSCard,
  MDSCardVariant,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import type { AgentGoal } from "@/pages/agent/intent-graph/agent-intent-graph.types";
import { decodeAgentDisplayText } from "@/pages/agent/utils/agent-display-text.utils";
import { formatStatusLabel } from "@/pages/agent/utils/agent-status.utils";
import type { AssistantProjectObservable } from "@/store/assistant";

export const AgentGoalCard = ({
  descendants,
  goal,
  projects,
  onOpenGoal,
  onOpenProject,
}: {
  descendants: Array<{ goal: AgentGoal; depth: number }>;
  goal: AgentGoal;
  projects: AssistantProjectObservable[];
  onOpenGoal: ({ goalId }: { goalId: string }) => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
}) => {
  const goalTitle = decodeAgentDisplayText({ value: goal.title || "Untitled goal" });
  const openGoal = () => onOpenGoal({ goalId: goal.id });

  return (
    <StyledGoalTileInteraction
      aria-label={`Open ${goalTitle}`}
      role="group"
      tabIndex={0}
      onClick={openGoal}
      onKeyDown={event => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          openGoal();
        }
      }}
    >
      <StyledGoalTile variant={MDSCardVariant.Chrome}>
        <StyledTopRow>
          <StyledTitleBoundary onClick={event => event.stopPropagation()}>
            <StyledTitleLink variant={MDSTextVariant.HeaderS} onClick={openGoal}>
              {goalTitle}
            </StyledTitleLink>
          </StyledTitleBoundary>
          <StyledStatus color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
            {formatStatusLabel({ status: goal.status })}
          </StyledStatus>
        </StyledTopRow>

        <StyledDescription
          color={goal.description ? MDSTextColor.Secondary : MDSTextColor.Tertiary}
          numberOfLines={3}
          variant={MDSTextVariant.ParagraphS}
        >
          {decodeAgentDisplayText({ value: goal.description || "No description yet." })}
        </StyledDescription>

        <StyledSummary aria-label={`${goalTitle} connections`}>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
            {formatCount({ count: descendants.length, singular: "subgoal" })}
          </MDSText>
          <StyledSummaryDot aria-hidden="true">·</StyledSummaryDot>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
            {formatCount({ count: projects.length, singular: "project" })}
          </MDSText>
        </StyledSummary>

        {descendants.length > 0 ? (
          <StyledSection>
            <MDSText
              color={MDSTextColor.Primary}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.SemiBold}
            >
              Goal path
            </MDSText>
            <StyledSubgoalList aria-label={`${goalTitle} subgoals`} role="list">
              {descendants.slice(0, 4).map(({ goal: descendantGoal, depth }) => (
                <StyledSubgoalRow key={descendantGoal.id} $depth={depth} role="listitem">
                  <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.AngleRight} size={10} />
                  <StyledNestedLinkBoundary onClick={event => event.stopPropagation()}>
                    <StyledNestedLink
                      truncate
                      onClick={() => onOpenGoal({ goalId: descendantGoal.id })}
                    >
                      {decodeAgentDisplayText({
                        value: descendantGoal.title || "Untitled goal",
                      })}
                    </StyledNestedLink>
                  </StyledNestedLinkBoundary>
                </StyledSubgoalRow>
              ))}
              {descendants.length > 4 ? (
                <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                  +{descendants.length - 4} more subgoals
                </MDSText>
              ) : null}
            </StyledSubgoalList>
          </StyledSection>
        ) : null}

        {projects.length > 0 ? (
          <StyledSection>
            <MDSText
              color={MDSTextColor.Primary}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.SemiBold}
            >
              Related projects
            </MDSText>
            <StyledProjectList>
              {projects.slice(0, 3).map(project => (
                <StyledProjectLinkBoundary
                  key={project.id}
                  onClick={event => event.stopPropagation()}
                >
                  <StyledProjectLink
                    truncate
                    onClick={() => onOpenProject({ projectId: project.id })}
                  >
                    <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.Folder} size={12} />
                    {decodeAgentDisplayText({ value: project.title || "Untitled project" })}
                  </StyledProjectLink>
                </StyledProjectLinkBoundary>
              ))}
            </StyledProjectList>
          </StyledSection>
        ) : null}
      </StyledGoalTile>
    </StyledGoalTileInteraction>
  );
};

const formatCount = ({ count, singular }: { count: number; singular: string }) =>
  `${count} ${count === 1 ? singular : `${singular}s`}`;

const StyledGoalTileInteraction = styled.div`
  cursor: pointer;
  min-width: 0;

  &:focus-visible {
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;

const StyledGoalTile = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  height: 100%;
  min-height: 240px;
  min-width: 0;

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.background.list.hover};
  }
`;

const StyledTopRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
`;

const StyledStatus = styled(MDSText)`
  background: ${({ theme }) => theme.colors.background.button.chip.default};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
`;

const StyledTitleBoundary = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const StyledTitleLink = styled(AgentLinkButton)`
  max-width: 100%;
  overflow-wrap: anywhere;
  text-align: left;
  white-space: normal;

  && {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.fontWeights.smedium};
    text-decoration: none;
  }
`;

const StyledDescription = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledSummary = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledSummaryDot = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledSubgoalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledSubgoalRow = styled.div<{ $depth: number }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-left: ${({ $depth, theme }) =>
    $depth > 1 ? `calc(${$depth - 1} * ${theme.spacing[3]})` : 0};
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
`;

const StyledNestedLinkBoundary = styled.div`
  min-width: 0;
`;

const StyledNestedLink = styled(AgentLinkButton)`
  && {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextS]};
    text-decoration: none;
  }
`;

const StyledProjectList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledProjectLinkBoundary = styled.div`
  min-width: 0;
`;

const StyledProjectLink = styled(AgentLinkButton)`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.chip.default};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  max-width: 220px;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};

  && {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
    text-decoration: none;
  }
`;
